import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { decryptUrlToken, decryptUrlPayload } from './CryptoUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  token: string | null;
  refreshToken: string | null;
  userInfo: Record<string, unknown> | null;
  logout: () => void;
}

const DEFAULT_STATE: Omit<AuthState, 'logout'> = {
  isAuthenticated: false,
  isAdmin: false,
  isLoading: false, // resolved synchronously; async path (encrypted token) sets true briefly
  token: null,
  refreshToken: null,
  userInfo: null,
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthState>({ ...DEFAULT_STATE, logout: () => {} });

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'mh_kc_token';
const REFRESH_TOKEN_KEY = 'mh_kc_refresh_token';
const DE_URL = import.meta.env.VITE_DE_URL || 'http://localhost:3000';
/**
 * Keycloak token endpoint for silent refresh. Constructed from env vars so
 * it works in any deployment without code changes.
 * NOTE: If Keycloak uses a self-signed TLS cert the browser must already
 * trust it (users who logged in via Project 1 have already accepted it).
 */
const _KC_BASE = `${
  (import.meta.env.VITE_KEYCLOAK_URL as string | undefined) || 'https://localhost:8443'
}/realms/${
  (import.meta.env.VITE_KEYCLOAK_REALM as string | undefined) || 'digital-employee'
}`;
const KEYCLOAK_TOKEN_URL = `${_KC_BASE}/protocol/openid-connect/token`;
/**
 * Keycloak session revocation endpoint. Calling this with a valid refresh token
 * invalidates the ENTIRE SSO session — both MH and DE are forced to re-login.
 */
const KEYCLOAK_LOGOUT_URL = `${_KC_BASE}/protocol/openid-connect/logout`;
const KEYCLOAK_CLIENT_ID =
  (import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string | undefined) || 'digital-employee-ui';
/**
 * How often (ms) to check token expiry while the user is in the app.
 * When the access token is expired or expiring soon the periodic check
 * attempts a silent refresh via the stored refresh token. Only redirects
 * to Project 1 when the refresh itself fails (refresh token expired/revoked).
 */
const EXPIRY_CHECK_INTERVAL_MS = 15_000; // 15 s — fast enough to detect DE logout within 15s
/** Proactively refresh when fewer than this many seconds remain on the token. */
const REFRESH_THRESHOLD_SECONDS = 70;

/** Realm roles that grant admin access. */
const ADMIN_ROLES = ['digital-employee-admin'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isTokenAlive(payload: Record<string, unknown>): boolean {
  const exp = payload?.exp as number | undefined;
  if (!exp) return false;
  return Date.now() / 1000 < exp;
}

/** Returns true when fewer than REFRESH_THRESHOLD_SECONDS remain before expiry. */
function isTokenExpiringSoon(payload: Record<string, unknown>): boolean {
  const exp = payload?.exp as number | undefined;
  if (!exp) return true;
  return Date.now() / 1000 > exp - REFRESH_THRESHOLD_SECONDS;
}

function hasAdminRole(payload: Record<string, unknown>): boolean {
  const roles = (payload?.realm_access as { roles?: string[] })?.roles ?? [];
  const granted = ADMIN_ROLES.some((r) => roles.includes(r));
  console.log('[AuthContext] realm_access.roles:', roles, '| admin granted:', granted);
  return granted;
}

function validateToken(raw: string): { token: string; payload: Record<string, unknown> } | null {
  const payload = decodeJwtPayload(raw);
  if (!payload) return null;
  if (!isTokenAlive(payload)) {
    console.warn('[AuthContext] Token expired. exp:', new Date((payload.exp as number) * 1000).toISOString());
    return null;
  }
  return { token: raw, payload };
}

// ---------------------------------------------------------------------------
// Synchronous bootstrap — localStorage + plain kc_token URL param
//
// Runs inside useState() initializer, BEFORE any render or React Router
// redirect can strip URL query params.
//
// NOTE: The encrypted param (kc_enc_token) requires async decryption and is
//       handled in the useEffect below.
// ---------------------------------------------------------------------------

function readTokenSync(): { token: string; payload: Record<string, unknown>; isEncrypted: boolean; refreshToken?: string | null; fromStorage: boolean } | null {
  const urlParams = new URLSearchParams(window.location.search);

  // If an encrypted payload or token is pending in the URL, signal that async work is needed.
  if (urlParams.has('kc_enc_payload') || urlParams.has('kc_enc_token')) {
    console.log('[AuthContext] Encrypted payload/token detected in URL — async decrypt needed.');
    return null; // isLoading stays true; useEffect will decrypt
  }

  // Plain URL token (legacy / fallback if Project 1 has not yet added encryption)
  const urlToken = urlParams.get('kc_token');
  if (urlToken) {
    console.warn('[AuthContext] Plain (unencrypted) token in URL — consider upgrading Project 1 to use kc_enc_token.');
    urlParams.delete('kc_token');
    window.history.replaceState({}, '', window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : ''));
    const result = validateToken(urlToken);
    if (result) {
      sessionStorage.setItem(TOKEN_KEY, urlToken);
      console.log('[AuthContext] Plain URL token accepted. sub:', result.payload.sub);
      return { ...result, isEncrypted: false, fromStorage: false };
    }
    return null;
  }

  // sessionStorage (page reload — token persists within the same browser tab)
  const stored = sessionStorage.getItem(TOKEN_KEY);
  if (stored) {
    console.log('[AuthContext] Token found in sessionStorage.');
    const result = validateToken(stored);
    if (result) {
      console.log('[AuthContext] sessionStorage token valid. sub:', result.payload.sub);
      const storedRefresh = sessionStorage.getItem(REFRESH_TOKEN_KEY);
      // fromStorage: false — no /userinfo verification needed (removed; DE logout
      // detection handled by /api/auth/session-status polling every 15s)
      return { ...result, isEncrypted: false, refreshToken: storedRefresh, fromStorage: false };
    }
    console.warn('[AuthContext] sessionStorage token expired — clearing.');
    sessionStorage.removeItem(TOKEN_KEY);
  }

  return null;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<Omit<AuthState, 'logout'>>(() => {
    const result = readTokenSync();
    if (result) {
      const isAdmin = hasAdminRole(result.payload);
      console.log(
        `[AuthContext] Sync auth — isAuthenticated: true | isAdmin: ${isAdmin} | user:`,
        result.payload.preferred_username ?? result.payload.sub
      );
      // fromStorage is always false now — isLoading resolved synchronously
      return { isAuthenticated: true, isAdmin, isLoading: false, token: result.token, refreshToken: result.refreshToken ?? null, userInfo: result.payload };
    }
    // If an encrypted payload/token is present in the URL, keep isLoading: true so
    // ProtectedRoute shows a spinner instead of redirecting to /unauthorized
    // before the async decryption useEffect has a chance to run.
    const urlP = new URLSearchParams(window.location.search);
    const pendingEncryptedToken = urlP.has('kc_enc_payload') || urlP.has('kc_enc_token');
    return { ...DEFAULT_STATE, isLoading: pendingEncryptedToken };
  });

  const setAuthenticated = useCallback((rawToken: string, payload: Record<string, unknown>, rawRefreshToken?: string | null) => {
    const isAdmin = hasAdminRole(payload);
    console.log(`[AuthContext] Async auth — isAdmin: ${isAdmin} | user:`, payload.preferred_username ?? payload.sub);
    sessionStorage.setItem(TOKEN_KEY, rawToken);
    if (rawRefreshToken) sessionStorage.setItem(REFRESH_TOKEN_KEY, rawRefreshToken);
    setState({ isAuthenticated: true, isAdmin, isLoading: false, token: rawToken, refreshToken: rawRefreshToken ?? null, userInfo: payload });
  }, []);

  // ---------------------------------------------------------------------------
  // NOTE: Keycloak /userinfo verification on page load was removed.
  // DE logout detection is handled by the /api/auth/session-status polling
  // (every 15s in the periodic check below). Token validity is checked locally
  // via JWT exp. This avoids CSP issues with direct browser→Keycloak calls.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // ASYNC INIT: Encrypted URL params (?kc_enc_payload=... or ?kc_enc_token=...)
  //
  // kc_enc_payload (new): JSON payload { token, refresh_token } — preferred.
  // kc_enc_token  (legacy): single access token only — backward compat.
  // Plain tokens (?kc_token=) and sessionStorage are handled synchronously above.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (state.isAuthenticated) return;

    const decryptEncryptedToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const encPayload = urlParams.get('kc_enc_payload');
      const encToken = urlParams.get('kc_enc_token');

      if (!encPayload && !encToken) {
        // No encrypted param — auth already resolved (or no token at all)
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      // ── New format: kc_enc_payload = encrypted JSON { token, refresh_token } ──
      if (encPayload) {
        console.log('[AuthContext] Decrypting encrypted URL payload (token + refresh_token)...');
        urlParams.delete('kc_enc_payload');
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '')
        );

        const decryptedJson = await decryptUrlPayload(encPayload);
        if (decryptedJson) {
          const { token: rawToken, refresh_token: rawRefresh } = decryptedJson;
          const result = validateToken(rawToken);
          if (result) {
            setAuthenticated(rawToken, result.payload, rawRefresh || null);
            return;
          }
          console.warn('[AuthContext] Decrypted payload token is invalid/expired.');
        }
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      // ── Legacy format: kc_enc_token = encrypted raw JWT string ──
      if (encToken) {
        console.log('[AuthContext] Decrypting legacy encrypted URL token...');
        urlParams.delete('kc_enc_token');
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '')
        );

        const rawToken = await decryptUrlToken(encToken);
        if (rawToken) {
          const result = validateToken(rawToken);
          if (result) {
            setAuthenticated(rawToken, result.payload, null);
            return;
          }
          console.warn('[AuthContext] Decrypted token is invalid/expired.');
        }
      }

      setState((s) => ({ ...s, isLoading: false }));
    };

    decryptEncryptedToken();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // SILENT TOKEN REFRESH + PERIODIC EXPIRY CHECK
  //
  // Every 60 s: if the access token has expired or will expire within
  // REFRESH_THRESHOLD_SECONDS, a silent refresh is attempted via the stored
  // Keycloak refresh token.  Memory Hub is only redirected to Project 1 when
  // the refresh itself fails (refresh token expired / revoked / network error).
  // ---------------------------------------------------------------------------
  const expiryCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isRefreshingRef = useRef(false);

  const performTokenRefresh = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) return false;
    const storedRefresh = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!storedRefresh) {
      console.warn('[AuthContext] No refresh token stored — cannot perform silent refresh.');
      return false;
    }

    isRefreshingRef.current = true;
    console.log('[AuthContext] Attempting silent token refresh via backend proxy...');
    try {
      // POST to MH backend proxy — it adds client_secret server-side.
      // A direct browser → Keycloak call would be rejected for confidential
      // clients (missing client_secret) and would also fail silently for
      // self-signed TLS certs, causing MH to sign out every ~5 minutes.
      const response = await fetch('/api/auth/token-refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: storedRefresh }),
      });

      if (!response.ok) {
        console.warn('[AuthContext] Refresh request failed with status:', response.status);
        return false;
      }

      const data = await response.json() as { access_token?: string; refresh_token?: string };
      const newToken = data.access_token;
      const newRefresh = data.refresh_token;

      if (!newToken) return false;

      const result = validateToken(newToken);
      if (!result) {
        console.warn('[AuthContext] Refreshed token failed validation.');
        return false;
      }

      console.log(
        '[AuthContext] Token refreshed. New exp:',
        new Date((result.payload.exp as number) * 1000).toISOString()
      );
      sessionStorage.setItem(TOKEN_KEY, newToken);
      if (newRefresh) sessionStorage.setItem(REFRESH_TOKEN_KEY, newRefresh);
      setState((s) => ({ ...s, token: newToken, refreshToken: newRefresh ?? s.refreshToken }));
      return true;
    } catch (err) {
      console.error('[AuthContext] Token refresh network error:', err);
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!state.isAuthenticated) return;

    expiryCheckRef.current = setInterval(async () => {
      const stored = sessionStorage.getItem(TOKEN_KEY);
      if (!stored) {
        console.warn('[AuthContext] Periodic check: access token missing — signing out.');
        clearInterval(expiryCheckRef.current!);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        setState({ ...DEFAULT_STATE, isLoading: false });
        window.location.href = DE_URL;
        return;
      }

      const result = validateToken(stored);

      // ── Token fully expired: must refresh or sign out ────────────────────
      if (!result) {
        console.log('[AuthContext] Periodic check: token expired — attempting silent refresh.');
        const refreshed = await performTokenRefresh();
        if (!refreshed) {
          console.warn('[AuthContext] Token expired and refresh failed — signing out.');
          clearInterval(expiryCheckRef.current!);
          sessionStorage.removeItem(TOKEN_KEY);
          sessionStorage.removeItem(REFRESH_TOKEN_KEY);
          setState({ ...DEFAULT_STATE, isLoading: false });
          window.location.href = DE_URL;
        }
        return;
      }

      // ── DE logout detection via MH backend in-memory flag ─────────────────
      // When DE calls POST /api/auth/logout with its Bearer token, MH backend
      // decodes sub from the token and stores it. Polling with our own Bearer
      // token lets MH backend extract sub and check the flag server-side.
      // Sub never appears in URLs or logs.
      try {
        const statusResp = await fetch('/api/auth/session-status', {
          headers: { Authorization: `Bearer ${stored}` },
        });
        if (statusResp.ok) {
          const { active } = await statusResp.json() as { active: boolean };
          if (!active) {
            console.warn('[AuthContext] Periodic check: Keycloak session revoked (DE logged out) — signing out.');
            clearInterval(expiryCheckRef.current!);
            sessionStorage.removeItem(TOKEN_KEY);
            sessionStorage.removeItem(REFRESH_TOKEN_KEY);
            setState({ ...DEFAULT_STATE, isLoading: false });
            window.location.href = `${DE_URL}?action=logout`;
            return;
          }
        }
      } catch {
        // MH backend unreachable — skip check this tick, don't sign out
      }

      // ── Token expiring soon: proactive refresh only if refresh token exists ─
      // Do NOT redirect here — if proactive refresh fails the next 60 s tick
      // will catch the actual expiry and sign out then.
      if (isTokenExpiringSoon(result.payload)) {
        const storedRefresh = sessionStorage.getItem(REFRESH_TOKEN_KEY);
        if (storedRefresh) {
          console.log('[AuthContext] Token expiring soon — attempting proactive silent refresh.');
          await performTokenRefresh();
        } else {
          console.log('[AuthContext] Token expiring soon but no refresh token stored — will wait for actual expiry.');
        }
      }
    }, EXPIRY_CHECK_INTERVAL_MS);

    return () => {
      if (expiryCheckRef.current) clearInterval(expiryCheckRef.current);
    };
  }, [state.isAuthenticated, performTokenRefresh]);

  // ---------------------------------------------------------------------------
  // CROSS-ORIGIN LOGOUT SYNC via localStorage event
  //
  // When Project 1 explicitly calls the Memory Hub /logout route (see App.tsx),
  // it sets localStorage key 'mh_logout_signal'. This tab picks it up via the
  // 'storage' event (fires across all tabs of the SAME origin).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'mh_logout_signal' && e.newValue) {
        console.log('[AuthContext] Logout signal received from another tab/window.');
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem('mh_logout_signal');
        setState({ ...DEFAULT_STATE, isLoading: false });
        window.location.href = `${DE_URL}?action=logout`;
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const doLogout = useCallback(async () => {
    if (expiryCheckRef.current) clearInterval(expiryCheckRef.current);

    // ---------------------------------------------------------------------------
    // Mark this logout as MH-initiated BEFORE navigating away.
    // DE will echo back a POST /api/auth/logout after processing ?action=logout.
    // That echo-back must NOT poison logged_out_subs, because DE re-authenticates
    // using a cached/same-session token whose iat predates the logout_time, which
    // would cause session-status to sign the user out again 15s after re-login.
    // The self-logout marker causes the echo-back to be silently discarded.
    // Must be called before clearing sessionStorage (needs the current token).
    // ---------------------------------------------------------------------------
    const storedToken = sessionStorage.getItem(TOKEN_KEY);
    if (storedToken) {
      try {
        await fetch('/api/auth/self-logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        console.log('[AuthContext] Self-logout marker set on backend.');
      } catch { /* non-critical */ }
    }

    // ---------------------------------------------------------------------------
    // Revoke the Keycloak SSO session via the MH backend (not directly from
    // browser) — self-signed TLS certs cause direct browser fetch to fail silently.
    // ---------------------------------------------------------------------------
    const storedRefresh = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (storedRefresh) {
      try {
        await fetch('/api/auth/revoke', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: storedRefresh }),
        });
        console.log('[AuthContext] Keycloak SSO session revoked via backend.');
      } catch (err) {
        console.warn('[AuthContext] Backend revocation call failed (ignored):', err);
      }
    }

    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.setItem('mh_logout_signal', String(Date.now()));
    setState({ ...DEFAULT_STATE });
    window.location.href = `${DE_URL}?action=logout`;
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, logout: doLogout }}>
      {children}
    </AuthContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook + helper
// ---------------------------------------------------------------------------

export const useAuth = (): AuthState => useContext(AuthContext);

export const getStoredToken = (): string | null => sessionStorage.getItem(TOKEN_KEY);
